import moment from 'moment'
import type { Moment } from 'moment'
import I18n, { isTranslationKey } from 'APP/I18n'
import { logger } from 'APP/Lib/Datadog'

import type { NormalizedAppointment } from 'APP/Store/History/types'

export const APPOINTMENT_ACTIONS = {
  CANCEL: 'cancel',
  RESCHEDULE: 'reschedule',
} as const

export const FEE_TYPES = {
  LATE_CANCELLATION: 'member_late_cancellation_fee_amount',
  LATE_RESCHEDULING: 'member_late_rescheduling_fee_amount',
  NO_SHOW: 'member_no_show_fee_amount',
  ENCOUNTER_FEE: 'encounter_fee_amount',
} as const

/**
 * Determines if an appointment is a paid OC (Online Counselling) type
 */
export const isOCAppointment = (appointmentType?: string): boolean => {
  const OC_APPOINTMENT_TYPES = ['oc_initial', 'oc_fu']

  if (!appointmentType) return false
  return OC_APPOINTMENT_TYPES.includes(appointmentType)
}

/**
 * Determines if the appointment is within a specified time window of the current time
 * @param appointmentStartTime - The appointment start time
 * @param windowHours - The time window in hours (defaults to 24)
 */
export const isWithinTimeWindow = (
  appointmentStartTime: string | Moment,
  windowHours: number = 24
): boolean => {
  const startTime = moment(appointmentStartTime)
  const now = moment()
  const timeDifference = startTime.diff(now)
  const windowMs = windowHours * 60 * 60 * 1000

  return timeDifference <= windowMs && timeDifference > 0
}

/**
 * Gets cancellation/rescheduling/no-show fee from eligible services
 * Returns the fee amount or null if not found
 */
export const getAppointmentFee = (
  eligibleServices: any,
  feeType: (typeof FEE_TYPES)[keyof typeof FEE_TYPES]
): number | null => {
  try {
    const onlineCounsellingService = eligibleServices?.online_counselling

    if (!onlineCounsellingService) {
      return null
    }

    const fee = onlineCounsellingService[feeType]

    if (fee && !isNaN(Number(fee))) {
      return Number(fee)
    }

    return null
  } catch (error) {
    logger.error('Error getting appointment fee:', {}, error)
    return null
  }
}

export interface AppointmentManagementCopyConfig {
  title: string
  subtitle: string
  noticeTitle: string
  noticeBody: string[]
  confirmLabel: string
  footerNote?: string | null
  i18nKey: string
  i18nOptions: Record<string, unknown>
}

/**
 * Determines which copy to use for appointment cancellation/rescheduling
 * Based on appointment type and timing
 */
export const getAppointmentManagementCopy = (
  appointment: NormalizedAppointment,
  action: (typeof APPOINTMENT_ACTIONS)[keyof typeof APPOINTMENT_ACTIONS],
  eligibleServices?: any,
  paymentInfo?: any
): AppointmentManagementCopyConfig => {
  const appointmentType = appointment.appointment_type
  const isOC = isOCAppointment(appointmentType)
  const within24Hours = isWithinTimeWindow(appointment.start_at)
  const feeType =
    action === APPOINTMENT_ACTIONS.CANCEL
      ? FEE_TYPES.LATE_CANCELLATION
      : FEE_TYPES.LATE_RESCHEDULING
  const fee = getAppointmentFee(eligibleServices, feeType)
  const noShowFee = getAppointmentFee(eligibleServices, FEE_TYPES.NO_SHOW)

  // For now - only show fee messaging if both noShow and service cancellation
  // fee information is provided by the service configuration
  const hasCompleteFees = noShowFee && fee

  // Select the copy (i18nKey + i18n options) based on appointment type and timing
  const i18nOptions: { fee?: number | null; noShowFee?: number | null; cardLast4?: string } = {}
  let i18nKey: `Appointments.${typeof action}AppointmentBottomSheet.${
    | 'default'
    | 'byType.oc.noFee'
    | 'byType.oc.withFee'}`

  // OC appointments
  if (isOC) {
    if (within24Hours && hasCompleteFees) {
      i18nKey = `Appointments.${action}AppointmentBottomSheet.byType.oc.withFee`

      i18nOptions.fee = fee
      i18nOptions.noShowFee = noShowFee
      i18nOptions.cardLast4 = paymentInfo?.last4Digits || '1234'
    } else {
      i18nKey = `Appointments.${action}AppointmentBottomSheet.byType.oc.noFee`
    }
  } else {
    // Default copy for non-OC appointments (B2B2C)
    i18nKey = `Appointments.${action}AppointmentBottomSheet.default`
  }

  const title = I18n.t(`${i18nKey}.title`, i18nOptions)
  const subtitle = I18n.t(`${i18nKey}.subtitle`, i18nOptions)
  const confirmLabel = I18n.t(`${i18nKey}.cta`, i18nOptions)
  const noticeTitle = I18n.t(`${i18nKey}.noticeTitle`, { ...i18nOptions, defaultValue: null })
  const noticeBody = I18n.t(`${i18nKey}.noticeBody`, {
    ...i18nOptions,
    returnObjects: true,
  })

  // Only get footerNote if it exists
  const footerNoteKey = `${i18nKey}.footerNote`
  const footerNote = isTranslationKey(footerNoteKey) ? I18n.t(footerNoteKey, i18nOptions) : null

  return {
    title,
    subtitle,
    noticeTitle,
    noticeBody,
    confirmLabel,
    footerNote,
    i18nKey,
    i18nOptions,
  }
}
